import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import Table from "../shared/table/Table";
import { Link } from "react-router-dom";
import { FiEdit3, FiTrash2, FiEye } from "react-icons/fi";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";
import useTranslations from "../../hooks/useTranslations";
import useUserLanguage from "../../hooks/useUserLanguage";

const WarehouseTable = () => {
  const [rows, setRows] = useState([]);
  const language = useUserLanguage();
  const { translations } = useTranslations(language);
  const getText = (key, defaultValue) => translations[key] || defaultValue;

  const fetchData = async () => {
    const res = await axios.get(
      `${process.env.REACT_APP_API_BASE_URL}/api/warehouses`,
    );
    setRows(res.data);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const toggleStatus = async (id, status) => {
    await axios.put(
      `${process.env.REACT_APP_API_BASE_URL}/api/warehouses/${id}/status`,
      {
        status: status === "active" ? "inactive" : "active",
      },
    );
    fetchData();
  };

  const toggleVisible = async (id, is_visible) => {
    await axios.put(
      `${process.env.REACT_APP_API_BASE_URL}/api/warehouses/${id}/is_visible`,
      {
        is_visible: is_visible ? 0 : 1,
      },
    );
    fetchData();
  };

  const toggleDefault = async (row) => {
    // ❌ aktif değilse
    if (row.status !== "active") {
      toast.error("Pasif depo varsayılan yapılamaz");
      return;
    }

    // ❌ görünür değilse
    if (!row.is_visible) {
      toast.error("Görünmeyen depo varsayılan yapılamaz");
      return;
    }

    await axios.put(
      `${process.env.REACT_APP_API_BASE_URL}/api/warehouses/${row.id}/is_default`,
      {
        is_default: !row.is_default,
      },
    );

    fetchData();
  };

  const handleDelete = (id) => {
    confirmAlert({
      title: getText("DESTROY_CONFIRM_TITLE"),
      message: getText("DESTROY_CONFIRM_MSG"),
      buttons: [
        {
          label: getText("YES_DELETE"),
          onClick: async () => {
            await axios.delete(
              `${process.env.REACT_APP_API_BASE_URL}/api/warehouses/${id}`,
            );
            toast.success(getText("SUCCESS_DELETED"));
            fetchData();
          },
        },
        { label: getText("CANCEL") },
      ],
    });
  };

  const Switch = ({ checked, onChange, disabled }) => (
    <div className="form-check form-switch text-end">
      <input
        className="form-check-input"
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        style={{
          cursor: disabled ? "not-allowed" : "pointer",
          width: 45,
          height: 22,
          opacity: disabled ? 0.5 : 1,
        }}
      />
    </div>
  );

  const columns = useMemo(
    () => [
      { accessorKey: "id", header: getText("TABLE_ID_LABEL"), size: 60 },
      { accessorKey: "title", header: getText("TABLE_TITLE_LABEL") },

      {
        accessorKey: "is_visible",
        header: getText("TABLE_VISIBLE_LABEL"),
        cell: ({ row }) => (
          <Switch
            checked={row.original.is_visible === 1}
            onChange={() =>
              toggleVisible(row.original.id, row.original.is_visible)
            }
          />
        ),
      },
      {
        accessorKey: "is_default",
        header: getText("TABLE_DEFAULT_LABEL", "Varsayılan"),
        cell: ({ row }) => {
          const data = row.original;

          const disabled = data.status !== "active" || !data.is_visible;

          return (
            <Switch
              checked={!!data.is_default}
              onChange={() => toggleDefault(data)}
              disabled={disabled}
              title={
                disabled ? "Default için depo aktif ve görünür olmalı" : ""
              }
            />
          );
        },
      },
      {
        accessorKey: "status",
        header: getText("TABLE_STATUS_LABEL"),
        cell: ({ row }) => (
          <Switch
            checked={row.original.status === "active"}
            onChange={() => toggleStatus(row.original.id, row.original.status)}
          />
        ),
      },

      {
        accessorKey: "actions",
        header: getText("TABLE_ACTIONS_LABEL"),
        cell: ({ row }) => (
          <div className="d-flex gap-2">
            <Link
              to={`/warehouses/view/${row.original.id}`}
              className="avatar-text avatar-md text-primary bg-soft-primary border-soft-primary"
              title={getText("VIEW")}
            >
              <FiEye />
            </Link>
            <Link
              to={`/warehouses/edit/${row.original.id}`}
              className="avatar-text avatar-md text-success bg-soft-success border-soft-success"
              title={getText("EDIT")}
            >
              <FiEdit3 />
            </Link>
            <button
              className="avatar-text avatar-md text-danger bg-soft-danger border-soft-danger"
              title={getText("DELETE")}
              onClick={() => handleDelete(row.original.id)}
            >
              <FiTrash2 />
            </button>
          </div>
        ),
      },
    ],
    [translations],
  );

  return <Table data={rows} columns={columns} />;
};

export default WarehouseTable;
